/**
 * The arithmetic behind tab overflow, extracted from `@salt-ds/core` and now ours.
 *
 * Pure functions on purpose: the whole overflow system reduces to "how wide is this" and "how
 * many of these fit", and both are worth being able to reason about without a DOM.
 */

/**
 * Below this, a width is not trusted.
 *
 * A tab that has not been laid out yet measures 0, and 0 is indistinguishable from a genuinely
 * empty tab. Treating an unmeasured tab as zero-width would let EVERY tab "fit", so the bar
 * would show them all and only collapse on a later pass — a visible flash of overflowing tabs.
 */
export const MIN_TRUSTED_TAB_WIDTH = 0.5;

/** Sub-pixel jitter from a resize is not a change worth re-measuring for. */
const WIDTH_EPSILON = 0.5;

export function getMeasuredWidth(element: HTMLElement | null | undefined): number {
  if (!element) return 0;
  const width = element.getBoundingClientRect().width;
  // `clientWidth` as a fallback: a `display: contents` ancestor gives an empty rect.
  return width || element.clientWidth || 0;
}

/**
 * The width a tab WANTS, not the width it currently has.
 *
 * A tab squeezed by its container reports a smaller box than its content needs, and deciding
 * overflow from the squeezed number is circular: the tab is narrow because it is overflowing.
 * `scrollWidth` is the content's own width, so this takes whichever is larger.
 */
export function getIntrinsicMeasuredWidth(element: HTMLElement | null | undefined): number {
  if (!element) return 0;
  const borderWidth = element.offsetWidth - element.clientWidth;
  return Math.max(getMeasuredWidth(element), element.scrollWidth + borderWidth);
}

export function getGapValue(styles: CSSStyleDeclaration): number {
  return Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
}

export function seedWidthMap(elements: HTMLElement[]): Map<HTMLElement, number> {
  return new Map(elements.map((element) => [element, getMeasuredWidth(element)]));
}

/** Record a new width, reporting whether it moved by more than the epsilon. */
export function updateWidthMap(
  widths: Map<HTMLElement, number>,
  element: HTMLElement,
  nextWidth: number,
  epsilon = WIDTH_EPSILON,
): boolean {
  const previous = widths.get(element);
  widths.set(element, nextWidth);
  // First sighting is never a change — there is nothing to compare against, and reporting one
  // would restart measurement on every newly observed element.
  if (previous === undefined) return false;
  return Math.abs(previous - nextWidth) > epsilon;
}

export interface MeasuredTab {
  value: string;
  width: number | null;
}

/**
 * How many tabs fit, in three passes.
 *
 * 1. Fill greedily until the next tab would not fit.
 * 2. If anything was left over, an overflow button is needed — and it takes room the first pass
 *    already spent. So charge its width and REMOVE tabs until the total fits again. Skipping
 *    this is the classic off-by-one where the last tab sits underneath the overflow button.
 * 3. If the selected tab is among the leftovers it is PINNED into view, evicting others as
 *    needed: a tab bar whose current tab is hidden inside a menu gives the user no way to see
 *    where they are.
 *
 * Returns `null` — not a count — when any tab's width is unknown, so the caller can retry rather
 * than commit to a layout computed from zeros.
 */
export function calculateVisibleCount({
  gap,
  maxWidth: initialMaxWidth,
  overflowWidth,
  pinnedValue,
  tabs,
}: {
  gap: number;
  maxWidth: number;
  overflowWidth: number;
  pinnedValue?: string | null;
  tabs: MeasuredTab[];
}): number | null {
  let maxWidth = initialMaxWidth;
  let currentWidth = 0;
  let count = 0;
  const visible: MeasuredTab[] = [];

  while (count < tabs.length) {
    const tab = tabs[count];
    if (!tab) break;
    if (tab.width == null) return null;
    const width = tab.width + gap;
    if (currentWidth + width > maxWidth) break;
    currentWidth += width;
    visible.push(tab);
    count += 1;
  }

  if (count >= tabs.length) return count;

  maxWidth -= overflowWidth;
  while (currentWidth > maxWidth) {
    const removed = visible.pop();
    if (!removed) break;
    if (removed.width == null) return null;
    currentWidth -= removed.width + gap;
    count -= 1;
  }

  const pinned = pinnedValue == null ? null : (tabs.find((t) => t.value === pinnedValue) ?? null);
  if (pinned && !visible.includes(pinned)) {
    if (pinned.width == null) return null;
    const pinnedWidth = pinned.width + gap;
    while (currentWidth + pinnedWidth > maxWidth) {
      const removed = visible.pop();
      if (!removed) break;
      if (removed.width == null) return null;
      currentWidth -= removed.width + gap;
      count -= 1;
    }
  }

  return Math.max(0, count);
}

/**
 * Split the ordered values into visible and hidden.
 *
 * The pinned value is APPENDED to the visible list rather than inserted in its original place.
 * That is deliberate and visible: a selected tab pulled out of the overflow appears at the end
 * of the strip, next to the overflow button it came from, instead of shifting every other tab
 * along to make room in the middle.
 */
export function partitionVisibleValues(
  orderedValues: string[],
  visibleCount: number,
  pinnedValue?: string | null,
): { visibleValues: string[]; hiddenValues: string[] } {
  let visibleValues = orderedValues.slice(0, visibleCount);
  let hiddenValues = orderedValues.slice(visibleCount);
  const pinnedIndex = pinnedValue != null ? hiddenValues.indexOf(pinnedValue) : -1;
  if (pinnedIndex !== -1) {
    const pinned = hiddenValues[pinnedIndex];
    hiddenValues = hiddenValues.filter((_, index) => index !== pinnedIndex);
    if (pinned !== undefined) visibleValues = [...visibleValues, pinned];
  }
  return { visibleValues, hiddenValues };
}

/** True for a real HTMLElement in the element's OWN document, not merely something object-like. */
export function isHTMLElement(node: unknown): node is HTMLElement {
  if (!node || typeof node !== "object") return false;
  const view = (node as Node).ownerDocument?.defaultView;
  // Checked against the node's own window: in an iframe or a portal to another document,
  // `instanceof HTMLElement` against OUR window is false for a perfectly real element.
  return !!view && node instanceof view.HTMLElement;
}
